/**
 * Scheduled Booking Service
 * 
 * Handles logic for loading, converting, and managing scheduled bookings
 */

import type { ScheduledBooking } from '@/types/scheduledBooking';
import type { TimesheetEntry } from '@/types/timesheet';
import { mockScheduledBookings } from '@/data/mockScheduledBookings';

/**
 * Get scheduled bookings for a specific employee and week
 */
export function getScheduledBookingsForWeek(
  employeeId: string,
  weekEnding: Date
): ScheduledBooking[] {
  return mockScheduledBookings.filter(
    booking =>
      booking.employeeId === employeeId &&
      booking.weekEnding.getTime() === weekEnding.getTime() &&
      booking.status === 'active'
  );
}

/**
 * Convert a scheduled booking to a timesheet entry
 */
export function convertBookingToEntry(booking: ScheduledBooking): TimesheetEntry {
  return {
    id: `entry-scheduled-${booking.id}`,
    projectId: booking.projectId,
    costCentreId: booking.costCentreId,
    description: booking.notes || '',
    hours: { ...booking.scheduledHours },
    // Scheduling metadata
    isFromSchedule: true,
    scheduledBookingId: booking.id,
    scheduledHours: { ...booking.scheduledHours },
    isDismissed: false,
    bookingCreatedBy: booking.createdBy,
    bookingCreatedByName: booking.createdByName,
    bookingNotes: booking.notes,
    bookingCreatedAt: booking.createdAt,
  };
}

/**
 * Check if hours have been edited from scheduled
 */
export function hasEditedHours(entry: TimesheetEntry): boolean {
  if (!entry.isFromSchedule || !entry.scheduledHours) {
    return false;
  }

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  
  return days.some(day => entry.hours[day] !== entry.scheduledHours![day]);
}

/**
 * Check if an entry conflicts with a scheduled booking
 * (same project and cost centre for the same week)
 */
export function hasConflict(
  entry: TimesheetEntry,
  scheduledBookings: ScheduledBooking[]
): ScheduledBooking | null {
  if (entry.isFromSchedule) {
    return null; // Scheduled entries don't conflict with themselves
  }

  const conflict = scheduledBookings.find(
    booking =>
      booking.projectId === entry.projectId &&
      booking.costCentreId === entry.costCentreId
  );

  return conflict || null;
}

/**
 * Dismiss a scheduled booking
 */
export function dismissBooking(
  entry: TimesheetEntry,
  reason: string
): TimesheetEntry {
  return {
    ...entry,
    isDismissed: true,
    dismissedAt: new Date(),
    dismissalReason: reason,
    // Clear hours when dismissed
    hours: {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    },
  };
}

/**
 * Restore a dismissed booking
 */
export function restoreBooking(entry: TimesheetEntry): TimesheetEntry {
  if (!entry.scheduledHours) {
    return entry;
  }

  return {
    ...entry,
    isDismissed: false,
    dismissedAt: undefined,
    dismissalReason: undefined,
    // Restore original scheduled hours
    hours: { ...entry.scheduledHours },
  };
}

/**
 * Auto-load scheduled bookings for a timesheet
 * This merges scheduled bookings with existing entries
 */
export function autoLoadScheduledBookings(
  employeeId: string,
  weekEnding: Date,
  existingEntries: TimesheetEntry[]
): TimesheetEntry[] {
  const scheduledBookings = getScheduledBookingsForWeek(employeeId, weekEnding);
  
  // Convert bookings to entries
  const scheduledEntries = scheduledBookings.map(convertBookingToEntry);
  
  // Filter out scheduled entries that already exist (by booking ID)
  const existingBookingIds = new Set(
    existingEntries
      .filter(e => e.scheduledBookingId)
      .map(e => e.scheduledBookingId)
  );
  
  const newScheduledEntries = scheduledEntries.filter(
    entry => !existingBookingIds.has(entry.scheduledBookingId)
  );
  
  // Combine existing entries with new scheduled entries
  // Scheduled entries go first
  return [...newScheduledEntries, ...existingEntries];
}

/**
 * Calculate total scheduled hours for an entry
 */
export function getTotalScheduledHours(entry: TimesheetEntry): number {
  if (!entry.scheduledHours) {
    return 0;
  }

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  return days.reduce((sum, day) => sum + entry.scheduledHours![day], 0);
}

/**
 * Calculate total actual hours for an entry
 */
export function getTotalActualHours(entry: TimesheetEntry): number {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  return days.reduce((sum, day) => sum + entry.hours[day], 0);
}
